/**
 * Infinite-scroll pagination for the external PA orders table: loads the next page when the
 * user reaches the end of the current result set.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../../data-model/filterValues";
import { RootState } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { makeAlgoliaCallForNextPage } from "../apis/makeAlgoliaCallForNextPage";
import { generateFilterPayloadForExternalOrders } from "../utils/generateFilterPayloadForExternalOrders";
import { useExternalFilterPayload } from "./useExternalFilterPayload";
import { ExternalTableState } from "./useExternalTableData";

const EXTERNAL_TABLE_NAME = "allMedicalPaOrdersForExternalByAlgolia";

/**
 * Handles infinite-scroll pagination by automatically fetching the next page of orders
 * when the user scrolls to the end of the current data set. Computes whether more data
 * is available and appends fetched results to existing state.
 *
 * @param validFilters - Currently active filter values
 * @param initialDataFetchingExecuted - Whether the initial data load has completed
 * @param searchTerm - The current free-text search query
 * @param externalTableState - Current orders state (used to determine page/total_pages)
 * @param setExternalTableState - State setter for orders loading/data lifecycle
 * @param setTableData - State setter for table row data
 * @returns endIndex and its setter, used by the table to signal the visible data boundary
 */
export const useExternalNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  externalTableState: ExternalTableState,
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useExternalFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setExternalTableStateRef = useRef(setExternalTableState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setExternalTableStateRef.current = setExternalTableState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setExternalTableState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (externalTableState.data?.hits?.length ?? 0) - 1 &&
      (externalTableState.data?.page ?? 0) <
        (externalTableState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    externalTableState.data?.hits?.length,
    externalTableState.data?.page,
    externalTableState.data?.total_pages,
  ]);

  const orgId = getOrgIdForFetchExternalWorklist();

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return generateFilterPayloadForExternalOrders(
        validFilters,
        searchTerm,
        orgId,
      );
    }
    return filterPayload;
  }, [filterPayload, validFilters, searchTerm, orgId]);

  const payloadFinalWithPage =
    useMemo<SearchForMedicalPaOrdersFromPostgresParams>(
      () => ({
        ...payloadFinal,
        page: (externalTableState.data?.page ?? 0) + 1,
      }),
      [payloadFinal, externalTableState.data?.page],
    );

  const currentPage = externalTableState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makeAlgoliaCallForNextPage(
      payloadFinalWithPage,
      authStatusOptions as AuthStatusOptionModel[],
      setExternalTableStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(EXTERNAL_TABLE_NAME, {
      lastBatchFetched: currentPage + 1,
    });
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
